import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Button, Heading, Skeleton } from '@indeed/ifl-components';
import { Bookmark } from '@indeed/ifl-icons';
import { Alert9 } from '@indeed/indeed-design-system-illustrations-spot';
import { SafeMarkup } from '@indeed/jspl-foundations';
import {
    useJobDetailsTabContentQuery,
    useJobDetailsTabContentSaveJobMutation,
} from './__generated__/JobDetailsTabContent.generated';
import GettextFactory from '../../../util/GettextFactory';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';

interface JobDetailsTabContentProps {
    jobKey: string;
}

export const JobDetailsTabContent = ({ jobKey }: JobDetailsTabContentProps) => {
    const gt = GettextFactory();
    const location = useLocation();
    const { draftId } = useParams<{ draftId: string }>();
    const eventLogger = useEventLogger();
    const [hasRetried, setHasRetried] = useState(false);
    const { data, loading, refetch } = useJobDetailsTabContentQuery({
        variables: { jobKeys: [jobKey] },
        notifyOnNetworkStatusChange: true,
        // IAFE-1434: This component cannot be rendered on server-side due to the usage of `SafeMarkup`.
        ssr: false,
        errorPolicy: 'all',
    });
    const [saveJob, { loading: saveJobLoading }] = useJobDetailsTabContentSaveJobMutation();

    if (loading) {
        return (
            <Box sx={{ padding: 5 }}>
                <Skeleton variant="text" sx={{ marginBlockEnd: 3 }} />
                <Skeleton variant="text" sx={{ inlineSize: '80%', marginBlockEnd: 3 }} />
                <Skeleton variant="text" sx={{ inlineSize: '60%', marginBlockEnd: 3 }} />
                <Skeleton variant="text" sx={{ marginBlockEnd: 3 }} />
                <Skeleton variant="text" sx={{ inlineSize: '70%' }} />
            </Box>
        );
    }
    const job = data?.jobData.results[0]?.job;
    const descriptionHtml = job?.description.html;

    // Show error only if there's no job data at all
    if (!job) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 5,
                }}
            >
                <Alert9 aria-hidden />
                <Heading level={3} sx={{ marginBlockStart: 6 }}>
                    {gt.gettext("We can't load this job")}
                </Heading>
                {!hasRetried && (
                    <Button
                        variant="secondary"
                        sx={{ marginBlockStart: 4 }}
                        onClick={() => {
                            eventLogger.logReviewButtonClick?.({
                                currentPage: location.pathname,
                                button: 'job-details-retry',
                                draftId: AvroOptional.string(draftId),
                            });
                            void refetch().catch(() => {});
                            setHasRetried(true);
                        }}
                    >
                        {gt.gettext('Try again')}
                    </Button>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
                padding: 5,
            }}
        >
            <Box>
                <Heading level={3} variant="subheading">
                    {gt.gettext('Job description')}
                </Heading>
                <Box sx={{ color: 'foreground' }}>
                    <SafeMarkup allowLinks>{descriptionHtml}</SafeMarkup>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Button
                    type="button"
                    variant="utility"
                    size="lg"
                    iconBefore={<Bookmark aria-hidden />}
                    onClick={() => {
                        if (job.savedJob?.state === 'SAVED' || saveJobLoading) {
                            return;
                        }
                        eventLogger.logReviewButtonClick?.({
                            currentPage: location.pathname,
                            button: 'job-details-save-job',
                            draftId: AvroOptional.string(draftId),
                        });
                        void saveJob({
                            variables: {
                                input: { jobKey, state: 'SAVED' },
                            },
                        }).catch(() => {});
                    }}
                    full
                >
                    {gt.gettext('Save job')}
                </Button>
            </Box>
        </Box>
    );
};
